/**
 * SendPublicFilesModal — reusable "pick files → email them" modal.
 *
 * Used from:
 *   • CustomerPage → CustomerDetail header ("Send files to this customer")
 *   • CustomerPage → PersonRow ("Send files to this person")
 *   • PublicFilesPage → per-file quick-send button
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   preselectedFileIds: number[]      — seed the checkbox state
 *   recipients: string[]              — pre-filled email chips (from customer/person)
 *   customerId?: string               — audit + CC bookkeeping
 *   title?: string                    — modal title
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, MagnifyingGlass, Funnel, CheckCircle, Warning, PaperPlaneTilt,
  Spinner, FileText, FileImage, FilePdf, FileVideo, Tag, BookmarkSimple,
  FloppyDisk, Trash, CaretDown,
} from "@phosphor-icons/react";
import {
  fetchPublicFiles, fetchPublicFileClasses, sendPublicFiles,
  fetchPublicFileTemplates, createPublicFileTemplate, deletePublicFileTemplate,
} from "@/lib/api";

function fileIconFor(filename, mime) {
  const lower = (filename || "").toLowerCase();
  if (lower.match(/\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|svg)$/) ||
      (mime || "").startsWith("image/")) return FileImage;
  if (lower.match(/\.(mp4|mov|avi|wmv|mkv|webm|m4v|mpeg)$/) ||
      (mime || "").startsWith("video/")) return FileVideo;
  if (lower.endsWith(".pdf") || (mime || "").includes("pdf")) return FilePdf;
  return FileText;
}

export default function SendPublicFilesModal({
  open, onClose,
  preselectedFileIds = [],
  recipients = [],
  customerId = "",
  title = "Send ACC / US Air documents",
}) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [selected, setSelected] = useState(() => new Set(preselectedFileIds.map(Number)));
  const [toEmails, setToEmails] = useState(
    (recipients || []).filter(Boolean).map((e) => String(e).trim().toLowerCase()),
  );
  const [emailInput, setEmailInput] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // ── Templates (saved selections) ──
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);    // dropdown
  const [savingTpl, setSavingTpl] = useState(false); // mini "save as" form
  const [tplName, setTplName] = useState("");
  const [tplScope, setTplScope] = useState("personal");
  const [tplBusy, setTplBusy] = useState(false);

  const reloadTemplates = useCallback(async () => {
    try {
      const r = await fetchPublicFileTemplates();
      setTemplates(r?.items || []);
    } catch {
      setTemplates([]);
    }
  }, []);

  // Load catalogue when modal opens
  const reload = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [f, c] = await Promise.all([
        fetchPublicFiles({ limit: 500 }),
        fetchPublicFileClasses(),
      ]);
      setFiles(f?.items || []);
      setClasses((c?.items || []).map((x) => x.file_class));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load files");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setResult(null);
      return;
    }
    reload();
    reloadTemplates();
    setSelected(new Set(preselectedFileIds.map(Number)));
    setToEmails((recipients || []).filter(Boolean).map((e) => String(e).trim().toLowerCase()));
    setEmailInput("");
    setNote("");
    setTplOpen(false);
    setSavingTpl(false);
    setTplName("");
  }, [open, reload, reloadTemplates]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return files.filter((f) => {
      if (cls && f.file_class !== cls) return false;
      if (!ql) return true;
      return (
        f.file_type.toLowerCase().includes(ql) ||
        (f.explanation || "").toLowerCase().includes(ql) ||
        f.file_class.toLowerCase().includes(ql)
      );
    });
  }, [files, q, cls]);

  // Group by class for a cleaner scan
  const grouped = useMemo(() => {
    const out = new Map();
    for (const f of filtered) {
      const key = f.file_class || "Uncategorized";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(f);
    }
    return Array.from(out.entries());
  }, [filtered]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const commitEmailInput = () => {
    const raw = emailInput.trim().toLowerCase();
    if (!raw) return;
    if (!raw.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return;
    if (toEmails.includes(raw)) {
      setEmailInput("");
      return;
    }
    setToEmails((e) => [...e, raw]);
    setEmailInput("");
  };

  const removeEmail = (r) => setToEmails((e) => e.filter((x) => x !== r));

  const canSend = selected.size > 0 && toEmails.length > 0 && !sending;

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await sendPublicFiles({
        file_ids: Array.from(selected),
        recipients: toEmails,
        customer_id: customerId || null,
        note: note || "",
      });
      setResult({ ok: true, ...r });
    } catch (e) {
      setResult({
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Send failed",
      });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="public-files-modal"
      className="fixed inset-0 z-[300] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-[92vw] max-w-3xl max-h-[90vh] rounded-md border border-slate-200 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <PaperPlaneTilt size={18} weight="bold" className="text-blue-700" />
          <h2 className="text-base font-bold font-['Chivo'] text-slate-900 flex-1 truncate">
            {title}
          </h2>
          <button
            data-testid="public-files-modal-close"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Search + class filter */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-2.5 text-slate-400" />
              <input
                data-testid="public-files-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search documents…"
                className="w-full h-9 pl-8 pr-2 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
              />
            </div>
            <div className="relative">
              <Funnel size={13} weight="bold" className="absolute left-2.5 top-2.5 text-slate-400" />
              <select
                data-testid="public-files-class-filter"
                value={cls}
                onChange={(e) => setCls(e.target.value)}
                className="h-9 pl-8 pr-6 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
              >
                <option value="">All classes ({classes.length})</option>
                {classes.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status */}
          {loading && (
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Spinner size={14} className="animate-spin" /> Loading catalogue…
            </div>
          )}
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{err}</div>}

          {/* Templates row */}
          {!loading && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <button
                  data-testid="public-files-templates-toggle"
                  onClick={() => setTplOpen((v) => !v)}
                  className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  <BookmarkSimple size={11} weight="bold" /> Apply template
                  <span className="text-slate-400 font-normal normal-case tracking-normal ml-1">
                    {templates.length > 0 ? `(${templates.length})` : ""}
                  </span>
                  <CaretDown size={9} weight="bold" />
                </button>
                {tplOpen && (
                  <div
                    data-testid="public-files-templates-dropdown"
                    className="absolute top-full mt-1 left-0 z-30 bg-white border border-slate-200 rounded-sm shadow-lg w-72 max-h-64 overflow-y-auto"
                  >
                    {templates.length === 0 && (
                      <div className="text-xs text-slate-400 text-center py-4">
                        No saved templates yet — pick files, then click "Save as template…".
                      </div>
                    )}
                    {templates.map((t) => (
                      <div
                        key={t.id}
                        data-testid={`public-files-template-${t.slug}`}
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        <button
                          onClick={() => {
                            setSelected(new Set((t.files || []).map((f) => f.id)));
                            setTplOpen(false);
                          }}
                          className="flex-1 text-left min-w-0"
                          data-testid={`public-files-apply-template-${t.slug}`}
                        >
                          <div className="text-xs font-bold text-slate-900 truncate">{t.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {(t.files || []).length} file{t.files?.length === 1 ? "" : "s"} · {t.scope}
                            {t.use_count > 0 ? ` · used ${t.use_count}×` : ""}
                          </div>
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete template "${t.name}"?`)) return;
                            try {
                              await deletePublicFileTemplate(t.id);
                              await reloadTemplates();
                            } catch (e) { /* swallow */ }
                          }}
                          data-testid={`public-files-delete-template-${t.slug}`}
                          title="Delete template"
                          className="w-6 h-6 flex items-center justify-center rounded-sm text-slate-300 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash size={11} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selected.size > 0 && !savingTpl && (
                <button
                  data-testid="public-files-save-template-btn"
                  onClick={() => setSavingTpl(true)}
                  className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100"
                >
                  <FloppyDisk size={11} weight="bold" /> Save as template…
                </button>
              )}
              {savingTpl && (
                <form
                  data-testid="public-files-save-template-form"
                  className="flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const name = tplName.trim();
                    if (!name) return;
                    setTplBusy(true);
                    try {
                      await createPublicFileTemplate({
                        name,
                        file_ids: Array.from(selected),
                        scope: tplScope,
                      });
                      await reloadTemplates();
                      setSavingTpl(false);
                      setTplName("");
                    } catch (e2) { /* swallow */ }
                    finally { setTplBusy(false); }
                  }}
                >
                  <input
                    autoFocus
                    data-testid="public-files-template-name-input"
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    placeholder="e.g. Discovery pack"
                    className="h-6 px-1.5 text-[11px] rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 w-44"
                  />
                  <select
                    data-testid="public-files-template-scope-select"
                    value={tplScope}
                    onChange={(e) => setTplScope(e.target.value)}
                    className="h-6 px-1 text-[10px] rounded-sm border border-slate-300 bg-white"
                  >
                    <option value="personal">Just me</option>
                    <option value="shared">Shared with team</option>
                  </select>
                  <button
                    type="submit"
                    data-testid="public-files-template-save-confirm"
                    disabled={tplBusy || !tplName.trim()}
                    className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-300"
                  >
                    {tplBusy ? "…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSavingTpl(false); setTplName(""); }}
                    className="text-slate-400 hover:text-slate-700 w-5 h-5 flex items-center justify-center"
                  >
                    <X size={10} />
                  </button>
                </form>
              )}
            </div>
          )}

          {/* File list */}
          {!loading && grouped.length === 0 && (
            <div data-testid="public-files-empty" className="text-xs text-slate-500 text-center py-6">
              No documents match your filter.
            </div>
          )}
          {!loading && grouped.length > 0 && (
            <div className="space-y-4">
              {grouped.map(([classKey, items]) => (
                <div key={classKey}>
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1 mb-1.5">
                    <Tag size={10} weight="bold" /> {classKey}
                    <span className="text-slate-300 normal-case tracking-normal font-normal">
                      · {items.length}
                    </span>
                  </h3>
                  <ul className="space-y-1">
                    {items.map((f) => {
                      const Icon = fileIconFor(f.file, "");
                      const checked = selected.has(f.id);
                      return (
                        <li
                          key={f.id}
                          data-testid={`public-file-row-${f.id}`}
                          className={`flex items-start gap-2.5 px-2.5 py-2 rounded-sm border cursor-pointer transition-colors ${
                            checked
                              ? "bg-blue-50 border-blue-300"
                              : "border-slate-200 hover:bg-slate-50"
                          }`}
                          onClick={() => toggle(f.id)}
                        >
                          <input
                            type="checkbox"
                            data-testid={`public-file-checkbox-${f.id}`}
                            checked={checked}
                            onChange={() => toggle(f.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                          />
                          <Icon size={15} weight="bold" className={checked ? "text-blue-700" : "text-slate-500"} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">{f.file_type}</div>
                            {f.explanation && (
                              <div className="text-[11px] text-slate-500 truncate">{f.explanation}</div>
                            )}
                            {!f.available_for_customer && (
                              <span className="inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1 py-0.5 rounded-sm">
                                Internal only
                              </span>
                            )}
                            {f.allowed_with_quote && (
                              <span className="inline-block mt-0.5 ml-1 text-[9px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-1 py-0.5 rounded-sm">
                                Quote-attachable
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer: recipients + note + send */}
        <div className="border-t border-slate-200 px-5 py-3 space-y-2 bg-slate-50/50">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Recipients
              <span className="text-slate-400 font-normal normal-case tracking-normal ml-1">
                · {toEmails.length} chosen
              </span>
            </label>
            <div className="flex flex-wrap items-center gap-1.5">
              {toEmails.map((e) => (
                <span
                  key={e}
                  data-testid={`public-files-recipient-chip-${e}`}
                  className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-700 bg-white border border-slate-200 rounded-sm pl-1.5 pr-1 h-6"
                >
                  {e}
                  <button
                    onClick={() => removeEmail(e)}
                    data-testid={`public-files-recipient-remove-${e}`}
                    className="text-slate-400 hover:text-red-600 w-4 h-4 flex items-center justify-center"
                    title="Remove"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                data-testid="public-files-recipient-input"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    commitEmailInput();
                  }
                }}
                onBlur={commitEmailInput}
                placeholder="Add email (Enter or comma)"
                className="flex-1 min-w-[180px] h-6 px-1.5 text-[11px] font-mono rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Note (optional)
            </label>
            <textarea
              data-testid="public-files-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Personal message to include in the email body"
              className="w-full px-2 py-1.5 text-xs rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 resize-none"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="public-files-send-btn"
              onClick={handleSend}
              disabled={!canSend}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-bold rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:bg-slate-300"
            >
              {sending ? (
                <><Spinner size={12} className="animate-spin" /> Sending…</>
              ) : (
                <><PaperPlaneTilt size={12} weight="bold" />
                  Send {selected.size} file{selected.size === 1 ? "" : "s"} to {toEmails.length} recipient{toEmails.length === 1 ? "" : "s"}
                </>
              )}
            </button>
            {result?.ok && (
              <span data-testid="public-files-send-ok" className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle size={12} weight="bold" /> Sent ({result.files_attached}/{result.files_requested} attached)
              </span>
            )}
            {result && !result.ok && (
              <span data-testid="public-files-send-err" className="text-[11px] font-bold text-red-700 inline-flex items-center gap-1">
                <Warning size={12} weight="bold" /> {result.error}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
